import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { giftCardsApi } from '../api/giftCards'
import EstadoBadgeGiftCard, { COLORES_GIFTCARD, LABELS_GIFTCARD } from '../components/EstadoBadgeGiftCard'
import EstadoChipsFilter from '../components/EstadoChipsFilter'
import Boton from '../components/Boton'
import { useStoredState } from '../hooks/useStoredState'
import { useAutoRefresh } from '../hooks/useAutoRefresh'
import { fmtFecha, fmtMonto } from '../utils/fecha'
import { colors, text, sx } from '../design/tokens'
import Paginador from '../components/Paginador'
import { useAuth } from '../context/AuthContext'

const CHIP_OPTIONS = ['activa', 'agotada', 'vencida', 'anulada'].map(v => ({
  value: v,
  label: LABELS_GIFTCARD[v],
  bg: COLORES_GIFTCARD[v].bg,
  color: COLORES_GIFTCARD[v].color,
}))

function Kpi({ label, valor, destacado }) {
  return (
    <div style={{ ...sx.card, padding: '12px 16px', flex: 1, minWidth: 150 }}>
      <div style={{ fontSize: text.meta, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, marginTop: 2, ...sx.mono, color: destacado ? colors.link : colors.text }}>{valor}</div>
    </div>
  )
}

/**
 * Up-side: la plata NUEVA que trajeron las gift cards. Una gift card no es una
 * venta nueva por sí sola (esa plata ya había entrado al comprarla); la venta
 * nueva es lo que el cliente pone ENCIMA al canjearla. Por eso se muestra la
 * cuenta completa —bruta − gift card = up-side— y no un número suelto.
 */
function PanelUpside({ up, onVerNV }) {
  const [abierto, setAbierto] = useState(false)
  if (!up || !up.ventas) return null

  const Nro = ({ label, valor, fuerte, signo }) => (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
      {signo && <span style={{ fontSize: 18, color: colors.textMeta, marginRight: 2 }}>{signo}</span>}
      <div>
        <div style={{ fontSize: text.meta, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>{label}</div>
        <div style={{ fontSize: fuerte ? 26 : 18, fontWeight: fuerte ? 700 : 600, ...sx.mono, color: fuerte ? colors.link : colors.text }}>
          $ {fmtMonto(valor)}
        </div>
      </div>
    </div>
  )

  return (
    <div style={{ ...sx.card, padding: '14px 16px', marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 18, flexWrap: 'wrap' }}>
          <Nro label="Venta con gift card" valor={up.venta_bruta} />
          <Nro label="Gift card canjeada" valor={up.canjeado} signo="−" />
          <Nro label="Ingreso de más (up-side)" valor={up.upside} fuerte signo="=" />
        </div>
        <Boton variante="ghost" sm onClick={() => setAbierto(a => !a)}>
          {abierto ? 'Ocultar detalle' : `Ver detalle (${up.ventas})`}
        </Boton>
      </div>

      <div style={{ fontSize: text.sec, color: colors.textSec, marginTop: 8 }}>
        {up.ventas} {up.ventas === 1 ? 'venta canjeada' : 'ventas canjeadas'}
        {up.upside_por_peso != null && <> · por cada $1 de gift card entraron <strong>$ {fmtMonto(up.upside_por_peso)}</strong> nuevos</>}
        {up.canjes_sin_venta > 0 && <> · {up.canjes_sin_venta} canje(s) sin venta a la vista, no se cuentan</>}
      </div>

      {abierto && (
        <div style={{ marginTop: 12, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: text.body }}>
            <thead>
              <tr style={{ background: colors.bgSoft }}>
                {['Venta', 'Fecha', 'Cliente', 'Gift card', 'Venta bruta', 'Canjeado', 'Up-side'].map(h => (
                  <th key={h} style={{ ...sx.th, textAlign: ['Venta bruta', 'Canjeado', 'Up-side'].includes(h) ? 'right' : 'left' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {up.detalle.map(d => (
                <tr
                  key={d.nota_venta_id}
                  onClick={() => onVerNV(d.nota_venta_id)}
                  title="Ver la venta"
                  style={{ cursor: 'pointer', borderBottom: `1px solid ${colors.borderSoft}` }}
                  onMouseEnter={e => e.currentTarget.style.background = colors.bgSoft}
                  onMouseLeave={e => e.currentTarget.style.background = ''}
                >
                  <td style={{ padding: '8px 12px', ...sx.mono }}>{d.numero_presea || d.numero_lgr}</td>
                  <td style={{ padding: '8px 12px', ...sx.mono, color: colors.textSec }}>{fmtFecha(d.fecha)}</td>
                  <td style={{ padding: '8px 12px' }}>{d.cliente || '—'}</td>
                  <td style={{ padding: '8px 12px', ...sx.mono, color: colors.textSec }}>{d.codigos.join(' · ') || '—'}</td>
                  <td style={{ padding: '8px 12px', ...sx.mono, textAlign: 'right', whiteSpace: 'nowrap' }}>$ {fmtMonto(d.venta_bruta)}</td>
                  <td style={{ padding: '8px 12px', ...sx.mono, textAlign: 'right', whiteSpace: 'nowrap', color: colors.textSec }}>− $ {fmtMonto(d.canjeado)}</td>
                  <td style={{ padding: '8px 12px', ...sx.mono, textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 600 }}>$ {fmtMonto(d.upside)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {up.detalle_truncado > 0 && (
            <div style={{ fontSize: text.meta, color: colors.textMeta, padding: '8px 12px' }}>
              Se listan las {up.detalle.length} ventas más recientes — hay {up.detalle_truncado} más que sí están sumadas en los totales de arriba.
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default function GiftCardsPage() {
  const navigate = useNavigate()
  // Gift cards heredadas de Magento: solo las ve el super admin (Martin, 26 sep 2026).
  const { isSuperAdmin, impersonateRole } = useAuth()
  const veHeredadas = isSuperAdmin && !impersonateRole
  const [filtros, setFiltros] = useStoredState('lgr.filtros.giftcards', { estados: [], q: '' })
  const [data, setData] = useState({ data: [], current_page: 1, last_page: 1, total: 0 })
  const [resumen, setResumen] = useState(null)
  const [upside, setUpside] = useState(null)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)

  const cargar = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true)
    try {
      const [{ data: res }, { data: kpis }, { data: up }] = await Promise.all([
        giftCardsApi.listar({
          page,
          estado: filtros.estados.join(',') || undefined,
          q: filtros.q || undefined,
        }),
        giftCardsApi.resumen(),
        giftCardsApi.upside(),
      ])
      setData(res)
      setResumen(kpis)
      setUpside(up)
    } finally {
      setLoading(false)
    }
  }, [page, filtros])

  useEffect(() => { cargar() }, [cargar])
  useAutoRefresh(cargar, 30000)

  const filtrosActivos = filtros.estados.length > 0 || filtros.q

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 style={{ margin: 0 }}>
          Gift cards
          {filtrosActivos && <span style={{ marginLeft: 10, fontSize: text.sec, color: colors.link, fontWeight: 600 }}>· filtros activos</span>}
        </h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {veHeredadas && (
            <Boton variante="secondary" onClick={() => navigate('/gift-cards/heredadas')}>Heredadas de Magento</Boton>
          )}
          <Boton variante="primary" onClick={() => navigate('/gift-cards/nueva')}>+ Emitir gift card</Boton>
        </div>
      </div>

      {resumen && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
          <Kpi label="Circulante (activas)" valor={`$ ${fmtMonto(resumen.circulante)}`} destacado />
          <Kpi label="Activas" valor={resumen.activas} />
          <Kpi label="Vencen en 30 días" valor={resumen.por_vencer_30d} />
          <Kpi label="Canjeado últimos 30 días" valor={`$ ${fmtMonto(resumen.canjeado_30d)}`} />
        </div>
      )}

      <PanelUpside up={upside} onVerNV={id => navigate(`/notas-venta/${id}`)} />

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        <input
          placeholder="Buscar código, cliente o beneficiario..."
          value={filtros.q}
          onChange={e => { setFiltros({ ...filtros, q: e.target.value }); setPage(1) }}
          style={{ padding: '7px 10px', border: `1px solid ${colors.textPlh}`, borderRadius: 6, fontSize: text.body, width: 260 }}
        />
        <EstadoChipsFilter
          options={CHIP_OPTIONS}
          selected={filtros.estados}
          onToggle={v => {
            setFiltros({
              ...filtros,
              estados: filtros.estados.includes(v) ? filtros.estados.filter(x => x !== v) : [...filtros.estados, v],
            })
            setPage(1)
          }}
        />
        {filtrosActivos && (
          <Boton variante="ghost" sm onClick={() => { setFiltros({ estados: [], q: '' }); setPage(1) }}>Limpiar</Boton>
        )}
      </div>

      <div style={{ background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 10, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: text.body }}>
          <thead>
            <tr style={{ background: colors.bgSoft }}>
              {['Código', 'Estado', 'Valor', 'Saldo', 'Disponible', 'Cliente / Beneficiario', 'Vence', 'Emitida'].map(h => (
                <th key={h} style={{ ...sx.th }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && data.data.length === 0 && (
              <tr><td colSpan={8} style={{ padding: 24, textAlign: 'center', color: colors.textMeta }}>Cargando…</td></tr>
            )}
            {!loading && data.data.length === 0 && (
              <tr><td colSpan={8} style={{ padding: 24, textAlign: 'center', color: colors.textMeta }}>
                {filtrosActivos
                  ? 'No hay gift cards que coincidan con los filtros.'
                  : 'Sin gift cards. Se emiten solas al facturar una NV con GIF2500, o a mano con "+ Emitir gift card".'}
              </td></tr>
            )}
            {data.data.map(g => (
              <tr
                key={g.id}
                onClick={() => navigate(`/gift-cards/${g.id}`)}
                title="Ver gift card"
                style={{ cursor: 'pointer', borderBottom: `1px solid ${colors.borderSoft}` }}
                onMouseEnter={e => e.currentTarget.style.background = colors.bgSoft}
                onMouseLeave={e => e.currentTarget.style.background = ''}
              >
                <td style={{ padding: '10px 16px', ...sx.mono, fontWeight: 600 }}>{g.codigo}</td>
                <td style={{ padding: '10px 16px' }}>
                  <EstadoBadgeGiftCard estado={g.estado} />
                  {g.observada_at && <span title={g.observada_motivo || 'Observada'} style={{ marginLeft: 6, color: colors.warn, fontSize: text.sec }}>👁 Observada</span>}
                </td>
                <td style={{ padding: '10px 16px', ...sx.mono }}>$ {fmtMonto(g.monto_inicial)}</td>
                <td style={{ padding: '10px 16px', ...sx.mono }}>$ {fmtMonto(g.saldo)}</td>
                <td style={{ padding: '10px 16px', ...sx.mono, color: g.saldo_disponible < g.saldo ? colors.textSec : colors.text }}>
                  $ {fmtMonto(g.saldo_disponible)}
                </td>
                <td style={{ padding: '10px 16px' }}>
                  {g.cliente?.nombre || g.beneficiario_nombre || '—'}
                  {g.cliente && g.beneficiario_nombre && g.beneficiario_nombre !== g.cliente.nombre && (
                    <span style={{ color: colors.textSec }}> → {g.beneficiario_nombre}</span>
                  )}
                </td>
                <td style={{ padding: '10px 16px', ...sx.mono, color: colors.textSec }}>{g.vence_el ? fmtFecha(g.vence_el) : '—'}</td>
                <td style={{ padding: '10px 16px', ...sx.mono, color: colors.textSec }}>{fmtFecha(g.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Paginador
        pagina={data.current_page}
        totalPaginas={data.last_page}
        onCambiar={setPage}
        info={`${data.total} gift cards`}
      />
    </div>
  )
}
